import { useCallback, useEffect, useMemo, useState } from "react";
import { shouldShowAnchor, toKkiMonthParam } from "@karama/shared";
import { AnchorDisplayBlock } from "./AnchorDisplayBlock";
import {
	buildAnchorDisplayModel,
	buildCorrectionAnchorPreview,
	buildPreviewAnchorDisplayModel,
} from "./anchor-display-model";
import { DatePrecisionField } from "./DatePrecisionField";
import {
	type BundledKkiRate,
	resolveBundledKkiRate,
} from "./kki-bundled";
import {
	type DatePrecision,
	correctionDateChanged,
	formatPromiseDateBaseline,
	seedCorrectionOriginDate,
} from "./promise-metadata";
import { supabase } from "./supabase";

interface PromiseData {
	promise_id: string;
	type: "money" | "favor";
	direction: "outgoing" | "incoming";
	amount: string | null;
	currency: string | null;
	description: string | null;
	counterparty_phone: string;
	anchor: {
		type: "none" | "kki" | "usd" | "eur" | "xau";
		kk: string | null;
		kki_rate_origin: string | null;
		kki_version: string | null;
		country: string | null;
	};
	originated_at: string | null;
	date_precision: DatePrecision;
	created_at: string | null;
	recorder: { display_name: string | null; avatar_url: string | null } | null;
}

type PageState =
	| { kind: "loading" }
	| { kind: "expired" }
	| { kind: "already_responded" }
	| { kind: "ready"; data: PromiseData }
	| { kind: "submitted"; response: "confirmed" | "corrected" | "declined" }
	| { kind: "error"; message: string };

function flipDirection(d: "outgoing" | "incoming"): "outgoing" | "incoming" {
	return d === "outgoing" ? "incoming" : "outgoing";
}

/**
 * `promises.direction` — recorder POV (same encoding as mobile `amount.tsx`:
 * outgoing = I owe them, incoming = they owe me).
 * Coerces RPC JSON quirks; missing/stray values default conservatively so the
 * confirm headline and correction form stay in sync after `retry()`.
 */
function normalizeRecorderDirection(d: unknown): "outgoing" | "incoming" {
	const s = typeof d === "string" ? d.trim().toLowerCase() : "";
	if (s === "incoming") return "incoming";
	if (s === "outgoing") return "outgoing";
	return "outgoing";
}

function counterpartyPerspectiveFromRecorder(
	recorderDir: unknown,
): "outgoing" | "incoming" {
	return flipDirection(normalizeRecorderDirection(recorderDir));
}

/** Build client `PromiseData` from `confirm_get` JSON (anon key shapes). */
function promiseDataFromConfirmGetRpc(
	data: Record<string, unknown>,
): PromiseData | null {
	if (data.ok === false) return null;
	const anchor = data.anchor as Record<string, unknown> | undefined;
	return {
		promise_id: String(data.promise_id ?? ""),
		type: data.type === "favor" ? "favor" : "money",
		direction: normalizeRecorderDirection(data.direction),
		amount: data.amount != null ? String(data.amount) : null,
		currency: data.currency != null ? String(data.currency) : null,
		description:
			data.description != null ? String(data.description) : null,
		counterparty_phone: String(data.counterparty_phone ?? ""),
		anchor: {
			type: (anchor?.type as PromiseData["anchor"]["type"]) ?? "none",
			kk: anchor?.kk != null ? String(anchor.kk) : null,
			kki_rate_origin:
				anchor?.kki_rate_origin != null
					? String(anchor.kki_rate_origin)
					: null,
			kki_version:
				anchor?.kki_version != null ? String(anchor.kki_version) : null,
			country: anchor?.country != null ? String(anchor.country) : null,
		},
		originated_at:
			data.originated_at != null ? String(data.originated_at) : null,
		created_at:
			data.created_at != null ? String(data.created_at) : null,
		date_precision:
			(data.date_precision as DatePrecision) === "month" ||
			(data.date_precision as DatePrecision) === "year"
				? (data.date_precision as DatePrecision)
				: "day",
		recorder: data.recorder as PromiseData["recorder"],
	};
}

function seedsCorrectionInputsFromPromise(p: PromiseData): {
	amount: string;
	counterpartyDirection: "outgoing" | "incoming";
	originDate: string;
	datePrecision: DatePrecision;
} {
	return {
		amount: p.amount ?? "",
		counterpartyDirection: counterpartyPerspectiveFromRecorder(p.direction),
		originDate: seedCorrectionOriginDate(p.originated_at),
		datePrecision: p.date_precision,
	};
}

function normAmount(v: string | number | null | undefined): string {
	if (v == null || v === "") return "";
	return String(v).trim();
}

/** Recorder-perspective correction payload, or null if nothing changed vs promise. */
function buildCorrectionPayload(
	promise: PromiseData,
	corrAmount: string,
	corrDirection: "outgoing" | "incoming",
	corrOriginDate: string,
	corrDatePrecision: DatePrecision,
): Record<string, unknown> | null {
	const correction: Record<string, unknown> = {};

	const nextAmt = normAmount(corrAmount);
	const prevAmt = normAmount(promise.amount);
	if (nextAmt !== prevAmt) {
		correction.amount = nextAmt;
	}

	const corrDirAsRecorder = flipDirection(corrDirection);
	if (corrDirAsRecorder !== promise.direction) {
		correction.direction = corrDirAsRecorder;
	}

	if (
		correctionDateChanged({
			baselineOriginatedAt: promise.originated_at,
			baselineDatePrecision: promise.date_precision,
			pickedDayIso: corrOriginDate,
			pickedPrecision: corrDatePrecision,
		})
	) {
		const pickedDay = corrOriginDate.trim();
		correction.originated_at = new Date(
			`${pickedDay}T12:00:00.000Z`,
		).toISOString();
		correction.date_precision = corrDatePrecision;
	}

	return Object.keys(correction).length > 0 ? correction : null;
}

// Inline EN strings — the confirm page is counterparty-facing, EN-first
const copy: Record<string, string> = {
	perspectiveLabel: "%name says:",
	perspectiveOwesYou: "%name owes you",
	perspectiveYouOwe: "You owe %name",
	favorLabel: "Favor",
	sinceDate: "Since %date",
	sinceDateApprox: "Since ~%date",
	promiseDateRecorded: "Recorded %date",
	noOriginDateProvided: "No separate promise date was set",
	promiseDateLabel: "Promise date",
	correctionCurrentDate: "Currently: %date",
	anchoredLabel: "Inflation anchor",
	anchoredDetail: "Anchored: %detail",
	confirm: "Confirm",
	correct: "Correct",
	decline: "Decline",
	correctionTitle: "What needs correcting?",
	correctionAmount: "Amount",
	correctionDirection: "Direction",
	directionTheyOweMe: "%name owes you",
	directionIOweThemLabel: "You owe %name",
	correctionOriginDate: "Origin date",
	submitCorrection: "Submit Correction",
	cancelCorrection: "Cancel",
	linkExpiredTitle: "Link Expired",
	linkExpiredMessage: "This confirmation link is no longer valid. Ask the sender to resend it.",
	alreadyRespondedTitle: "Already Responded",
	alreadyRespondedMessage: "This promise has already been confirmed, corrected, or declined.",
	installCtaTitle: "Never lose track of a promise",
	installCtaMessage: "Download Karama — the app that keeps your word.",
	submitting: "Submitting\u2026",
	successTitle: "Done!",
	successConfirmed: "Promise confirmed.",
	successCorrected: "Correction submitted.",
	successDeclined: "Promise declined.",
	errorTitle: "Something went wrong",
	errorRetry: "Please try again.",
	correctionHint:
		"Adjust amount, direction, or origin date to propose a correction.",
	correctionNoChanges:
		"Change something before submitting — amount, direction, or origin date.",
};

function promiseDateSummary(promise: PromiseData): {
	primary: string;
	secondary?: string;
} {
	const baseline = formatPromiseDateBaseline({
		originatedAt: promise.originated_at,
		createdAt: promise.created_at,
		datePrecision: promise.date_precision,
	});

	if (baseline.kind === "origin") {
		return {
			primary: baseline.approx
				? txt("sinceDateApprox", { date: baseline.label })
				: txt("sinceDate", { date: baseline.label }),
		};
	}
	if (baseline.kind === "recorded") {
		return {
			primary: txt("promiseDateRecorded", { date: baseline.label }),
			secondary: txt("noOriginDateProvided"),
		};
	}
	if (import.meta.env.DEV) {
		console.warn(
			"[confirm-web] promise missing originated_at and created_at — apply migration 018?",
		);
	}
	return { primary: txt("noOriginDateProvided") };
}

function txt(key: string, vars?: Record<string, string | number>): string {
	let s = copy[key] ?? key;
	if (vars) {
		for (const [k, v] of Object.entries(vars)) {
			s = s.replaceAll(`%${k}`, String(v));
		}
	}
	return s;
}

// ---------------------------------------------------------------------------

export function ConfirmPage({ token }: { token: string }) {
	const [state, setState] = useState<PageState>({ kind: "loading" });
	const [showCorrection, setShowCorrection] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const [corrAmount, setCorrAmount] = useState("");
	const [corrDirection, setCorrDirection] = useState<"outgoing" | "incoming">("outgoing");
	const [corrOriginDate, setCorrOriginDate] = useState("");
	const [corrDatePrecision, setCorrDatePrecision] = useState<DatePrecision>("day");
	const [latestBundledRate, setLatestBundledRate] = useState<BundledKkiRate | null>(null);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			const { data, error } = await supabase.rpc("confirm_get", {
				p_token: token,
			});

			if (cancelled) return;

			if (error) {
				setState({ kind: "error", message: error.message });
				return;
			}

			if (!data || data.ok === false) {
				const reason = data?.reason;
				if (reason === "already_responded") {
					setState({ kind: "already_responded" });
				} else {
					setState({ kind: "expired" });
				}
				return;
			}

			const promise = promiseDataFromConfirmGetRpc(
				data as Record<string, unknown>,
			);
			if (!promise || !promise.promise_id) {
				setState({ kind: "error", message: txt("errorRetry") });
				return;
			}

			const seeds = seedsCorrectionInputsFromPromise(promise);
			setCorrAmount(seeds.amount);
			setCorrDirection(seeds.counterpartyDirection);
			if (seeds.originDate) {
				setCorrOriginDate(seeds.originDate);
				setCorrDatePrecision(seeds.datePrecision);
			} else {
				setCorrOriginDate("");
				setCorrDatePrecision("day");
			}

			setState({ kind: "ready", data: promise });
		})();

		return () => {
			cancelled = true;
		};
	}, [token]);

	const promiseSnapshot =
		state.kind === "ready" ? state.data : null;

	useEffect(() => {
		if (!promiseSnapshot || promiseSnapshot.type !== "money") {
			setLatestBundledRate(null);
			return;
		}
		if (!shouldShowAnchor({ anchorType: promiseSnapshot.anchor.type, anchorKkOrigin: null })) {
			setLatestBundledRate(null);
			return;
		}
		const country = promiseSnapshot.anchor.country ?? "MA";
		let cancelled = false;
		void resolveBundledKkiRate(country).then((rate) => {
			if (!cancelled) setLatestBundledRate(rate);
		});
		return () => {
			cancelled = true;
		};
	}, [promiseSnapshot]);

	const summaryAnchorModel = useMemo(() => {
		if (!promiseSnapshot || promiseSnapshot.type !== "money") return null;
		return buildAnchorDisplayModel({
			anchorType: promiseSnapshot.anchor.type,
			anchorKk: promiseSnapshot.anchor.kk,
			kkiVersion: promiseSnapshot.anchor.kki_version,
			country: promiseSnapshot.anchor.country,
			amount: promiseSnapshot.amount,
			bundledRate: latestBundledRate,
		});
	}, [promiseSnapshot, latestBundledRate]);

	const correctionPayloadIfReady = useMemo(() => {
		if (!promiseSnapshot) return null;
		return buildCorrectionPayload(
			promiseSnapshot,
			corrAmount,
			corrDirection,
			corrOriginDate,
			corrDatePrecision,
		);
	}, [
		promiseSnapshot,
		corrAmount,
		corrDirection,
		corrOriginDate,
		corrDatePrecision,
	]);

	const submitResponse = useCallback(
		async (response: "confirmed" | "corrected" | "declined") => {
			setSubmitting(true);

			let correction: Record<string, unknown> | null = null;
			if (response === "corrected" && state.kind === "ready") {
				const payload = buildCorrectionPayload(
					state.data,
					corrAmount,
					corrDirection,
					corrOriginDate,
					corrDatePrecision,
				);
				if (!payload) {
					setSubmitting(false);
					setState({
						kind: "error",
						message: txt("correctionNoChanges"),
					});
					return;
				}
				correction = payload;
			}

			const { data, error } = await supabase.rpc("confirm_respond", {
				p_token: token,
				p_response: response,
				p_correction: correction,
			});

			setSubmitting(false);

			if (error) {
				setState({ kind: "error", message: error.message });
				return;
			}

			if (data && data.ok === false) {
				if (data.reason === "invalid_or_expired") {
					setState({ kind: "expired" });
				} else {
					setState({ kind: "error", message: data.reason ?? txt("errorRetry") });
				}
				return;
			}

			setState({ kind: "submitted", response });
		},
		[token, state, corrAmount, corrDirection, corrOriginDate, corrDatePrecision],
	);

	const recorderName = useMemo(() => {
		if (state.kind !== "ready") return "";
		return state.data.recorder?.display_name ?? "Someone";
	}, [state]);

	const counterpartyDirection = useMemo(() => {
		if (state.kind !== "ready") return "incoming" as const;
		return counterpartyPerspectiveFromRecorder(state.data.direction);
	}, [state]);

	const retry = useCallback(() => {
		setState({ kind: "loading" });
		supabase
			.rpc("confirm_get", { p_token: token })
			.then(({ data, error }) => {
				if (error || !data || data.ok === false) {
					setState({ kind: "expired" });
					return;
				}
				const promise = promiseDataFromConfirmGetRpc(
					data as Record<string, unknown>,
				);
				if (!promise || !promise.promise_id) {
					setState({ kind: "expired" });
					return;
				}
				const seeds = seedsCorrectionInputsFromPromise(promise);
				setCorrAmount(seeds.amount);
				setCorrDirection(seeds.counterpartyDirection);
				if (seeds.originDate) {
					setCorrOriginDate(seeds.originDate);
					setCorrDatePrecision(seeds.datePrecision);
				} else {
					setCorrOriginDate("");
					setCorrDatePrecision("day");
				}
				setState({ kind: "ready", data: promise });
			});
	}, [token]);

	// ------- Render helpers -------

	if (state.kind === "loading") {
		return (
			<Layout>
				<div className="center-message">
					<div className="spinner" />
					<p className="edge-message">Loading…</p>
				</div>
			</Layout>
		);
	}

	if (state.kind === "expired") {
		return (
			<Layout showCta>
				<div className="center-message">
					<h2 className="edge-title">{txt("linkExpiredTitle")}</h2>
					<p className="edge-message">{txt("linkExpiredMessage")}</p>
				</div>
			</Layout>
		);
	}

	if (state.kind === "already_responded") {
		return (
			<Layout showCta>
				<div className="center-message">
					<h2 className="edge-title">{txt("alreadyRespondedTitle")}</h2>
					<p className="edge-message">{txt("alreadyRespondedMessage")}</p>
				</div>
			</Layout>
		);
	}

	if (state.kind === "error") {
		return (
			<Layout showCta>
				<div className="center-message">
					<h2 className="edge-title error-title">{txt("errorTitle")}</h2>
					<p className="edge-message">{state.message}</p>
				</div>
				<div className="actions">
					<button className="btn btn-secondary" onClick={retry}>
						Retry
					</button>
				</div>
			</Layout>
		);
	}

	if (state.kind === "submitted") {
		const successMap = {
			confirmed: txt("successConfirmed"),
			corrected: txt("successCorrected"),
			declined: txt("successDeclined"),
		} as const;

		return (
			<Layout showCta>
				<div className="center-message">
					<div className="success-icon">&#10003;</div>
					<h2 className="edge-title success-title">{txt("successTitle")}</h2>
					<p className="edge-message">{successMap[state.response]}</p>
				</div>
			</Layout>
		);
	}

	// --- state.kind === "ready" ---
	const promise = state.data;
	const isMoney = promise.type === "money";
	const displayAmount = promise.amount ?? "—";
	const displayCurrency = promise.currency ?? "";

	const perspectiveText =
		counterpartyDirection === "incoming"
			? txt("perspectiveOwesYou", { name: recorderName })
			: txt("perspectiveYouOwe", { name: recorderName });

	const dateSummary = promiseDateSummary(promise);

	return (
		<Layout showCta>
			<div className="content">
				<p className="perspective-label">
					{txt("perspectiveLabel", { name: recorderName })}
				</p>
				<p className="perspective-text">{perspectiveText}</p>

				{isMoney ? (
					<p className="amount-text">
						{displayAmount} {displayCurrency}
					</p>
				) : (
					<p className="perspective-text">
						{txt("favorLabel")}: {promise.description ?? "—"}
					</p>
				)}

				<div
					className="metadata-block"
					role="group"
					aria-label={txt("promiseDateLabel")}
				>
					<div className="metadata-row">
						<span className="metadata-key">{txt("promiseDateLabel")}</span>
						<span className="metadata-value">{dateSummary.primary}</span>
					</div>
					{dateSummary.secondary && (
						<p className="metadata-hint">{dateSummary.secondary}</p>
					)}
				</div>

				<span className={`direction-chip ${counterpartyDirection}`}>
					{counterpartyDirection === "incoming" ? "←" : "→"}{" "}
					{counterpartyDirection === "incoming"
						? txt("directionTheyOweMe", { name: recorderName })
						: txt("directionIOweThemLabel", { name: recorderName })}
				</span>

				{summaryAnchorModel && summaryAnchorModel.state !== "none" && (
					<div
						className="anchor-row"
						role="group"
						aria-label={txt("anchoredLabel")}
					>
						<span className="metadata-key">{txt("anchoredLabel")}</span>
						<AnchorDisplayBlock model={summaryAnchorModel} />
					</div>
				)}
			</div>

			<div className="actions">
				{showCorrection ? (
					<CorrectionForm
						promise={promise}
						recorderName={recorderName}
						corrAmount={corrAmount}
						setCorrAmount={setCorrAmount}
						corrDirection={corrDirection}
						setCorrDirection={setCorrDirection}
						corrOriginDate={corrOriginDate}
						setCorrOriginDate={setCorrOriginDate}
						corrDatePrecision={corrDatePrecision}
						setCorrDatePrecision={setCorrDatePrecision}
						latestBundledRate={latestBundledRate}
						summaryAnchorModel={summaryAnchorModel}
						hasCorrectionChanges={correctionPayloadIfReady !== null}
						submitting={submitting}
						onSubmit={() => submitResponse("corrected")}
						onCancel={() => setShowCorrection(false)}
					/>
				) : (
					<>
						<button
							className="btn btn-primary"
							disabled={submitting}
							onClick={() => submitResponse("confirmed")}
						>
							{submitting ? txt("submitting") : txt("confirm")}
						</button>
						<button
							className="btn btn-secondary"
							disabled={submitting}
							onClick={() => {
								const seeds = seedsCorrectionInputsFromPromise(promise);
								setCorrAmount(seeds.amount);
								setCorrDirection(seeds.counterpartyDirection);
								if (seeds.originDate) {
									setCorrOriginDate(seeds.originDate);
									setCorrDatePrecision(seeds.datePrecision);
								} else {
									setCorrOriginDate("");
									setCorrDatePrecision("day");
								}
								setShowCorrection(true);
							}}
						>
							{txt("correct")}
						</button>
						<button
							className="btn btn-danger"
							disabled={submitting}
							onClick={() => submitResponse("declined")}
						>
							{submitting ? txt("submitting") : txt("decline")}
						</button>
					</>
				)}
			</div>
		</Layout>
	);
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function Layout({
	children,
	showCta = false,
}: {
	children: React.ReactNode;
	showCta?: boolean;
}) {
	return (
		<div className="page">
			<header className="brand-header">
				<picture>
					<source srcSet="/karama-logo-white.png" media="(prefers-color-scheme: dark)" />
					<img
						src="/karama-logo-blue.png"
						alt="Karama"
						className="brand-logo"
					/>
				</picture>
			</header>
			{children}
			{showCta && (
				<footer className="install-cta">
					<div className="cta-card">
						<picture>
							<source srcSet="/karama-logo-white.png" media="(prefers-color-scheme: dark)" />
							<img
								src="/karama-logo-blue.png"
								alt="Karama"
								className="cta-logo"
							/>
						</picture>
						<p className="cta-headline">{txt("installCtaTitle")}</p>
						<p className="cta-subtitle">{txt("installCtaMessage")}</p>
						<div className="cta-badges">
							<a
								href="https://apps.apple.com"
								className="cta-badge-link"
								target="_blank"
								rel="noopener noreferrer"
							>
								<span className="store-badge">App Store</span>
							</a>
							<a
								href="https://play.google.com"
								className="cta-badge-link"
								target="_blank"
								rel="noopener noreferrer"
							>
								<span className="store-badge">Google Play</span>
							</a>
						</div>
					</div>
				</footer>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// CorrectionForm
// ---------------------------------------------------------------------------

interface CorrectionFormProps {
	promise: PromiseData;
	recorderName: string;
	corrAmount: string;
	setCorrAmount: (v: string) => void;
	corrDirection: "outgoing" | "incoming";
	setCorrDirection: (v: "outgoing" | "incoming") => void;
	corrOriginDate: string;
	setCorrOriginDate: (v: string) => void;
	corrDatePrecision: DatePrecision;
	setCorrDatePrecision: (p: DatePrecision) => void;
	latestBundledRate: BundledKkiRate | null;
	summaryAnchorModel: ReturnType<typeof buildAnchorDisplayModel> | null;
	hasCorrectionChanges: boolean;
	submitting: boolean;
	onSubmit: () => void;
	onCancel: () => void;
}

function CorrectionForm({
	promise,
	recorderName,
	corrAmount,
	setCorrAmount,
	corrDirection,
	setCorrDirection,
	corrDatePrecision,
	setCorrDatePrecision,
	corrOriginDate,
	setCorrOriginDate,
	latestBundledRate,
	summaryAnchorModel,
	hasCorrectionChanges,
	submitting,
	onSubmit,
	onCancel,
}: CorrectionFormProps) {
	const isMoney = promise.type === "money";
	const currentDateSummary = promiseDateSummary(promise);
	const [previewOriginRate, setPreviewOriginRate] = useState<BundledKkiRate | null>(
		null,
	);

	useEffect(() => {
		if (
			!summaryAnchorModel ||
			summaryAnchorModel.state !== "complete" ||
			!isMoney
		) {
			setPreviewOriginRate(null);
			return;
		}
		const country = promise.anchor.country ?? "MA";
		const month = corrOriginDate ? toKkiMonthParam(corrOriginDate) : undefined;
		let cancelled = false;
		void resolveBundledKkiRate(country, month).then((rate) => {
			if (!cancelled) setPreviewOriginRate(rate);
		});
		return () => {
			cancelled = true;
		};
	}, [
		summaryAnchorModel,
		isMoney,
		promise.anchor.country,
		corrOriginDate,
	]);

	const previewAnchorModel = useMemo(() => {
		if (
			!summaryAnchorModel ||
			summaryAnchorModel.state !== "complete" ||
			!isMoney
		) {
			return null;
		}
		const storedRate = promise.anchor.kki_rate_origin
			? Number.parseFloat(promise.anchor.kki_rate_origin)
			: null;
		const preview = buildCorrectionAnchorPreview({
			recordedKk: summaryAnchorModel.recordedKk!,
			amount: corrAmount || promise.amount || "0",
			storedRate,
			bundledRate: previewOriginRate ?? latestBundledRate,
			originDateIso: corrOriginDate,
		});
		if (preview.previewKk == null) return null;
		return buildPreviewAnchorDisplayModel({
			recordedKk: summaryAnchorModel.recordedKk!,
			previewKk: preview.previewKk,
			kkiVersion: promise.anchor.kki_version,
			country: promise.anchor.country,
			bundledRate: previewOriginRate ?? latestBundledRate,
		});
	}, [
		summaryAnchorModel,
		isMoney,
		promise,
		corrAmount,
		latestBundledRate,
		previewOriginRate,
		corrOriginDate,
	]);

	const showAnchorPreview =
		previewAnchorModel &&
		summaryAnchorModel &&
		(previewAnchorModel.pillLabel !== summaryAnchorModel.pillLabel ||
			previewAnchorModel.detailLine !== summaryAnchorModel.detailLine);

	return (
		<div className="correction-form">
			<h3 className="correction-title">{txt("correctionTitle")}</h3>

			{!hasCorrectionChanges && (
				<p className="correction-hint">{txt("correctionHint")}</p>
			)}

			{isMoney && (
				<div className="field-group">
					<label className="field-label">
						{txt("correctionAmount")} ({promise.currency ?? ""})
					</label>
					<input
						type="number"
						className="text-input"
						value={corrAmount}
						onChange={(e) => setCorrAmount(e.target.value)}
						placeholder={promise.amount ?? "0"}
					/>
				</div>
			)}

			<div className="field-group">
				<label className="field-label">{txt("correctionDirection")}</label>
				<div className="segmented-control">
					<button
						type="button"
						className={corrDirection === "incoming" ? "active" : ""}
						onClick={() => setCorrDirection("incoming")}
					>
						{txt("directionTheyOweMe", { name: recorderName })}
					</button>
					<button
						type="button"
						className={corrDirection === "outgoing" ? "active" : ""}
						onClick={() => setCorrDirection("outgoing")}
					>
						{txt("directionIOweThemLabel", { name: recorderName })}
					</button>
				</div>
				<div className="direction-preview">
					<span>{corrDirection === "incoming" ? "←" : "→"}</span>
					<span>
						{corrDirection === "incoming"
							? txt("directionTheyOweMe", { name: recorderName })
							: txt("directionIOweThemLabel", { name: recorderName })}
					</span>
				</div>
			</div>

			<div className="field-group">
				<label className="field-label">{txt("correctionOriginDate")}</label>
				<p className="correction-current-date">
					{txt("correctionCurrentDate", { date: currentDateSummary.primary })}
				</p>
				<DatePrecisionField
					value={corrOriginDate}
					precision={corrDatePrecision}
					onChange={(dayIso, precision) => {
						setCorrOriginDate(dayIso);
						setCorrDatePrecision(precision);
					}}
				/>
			</div>

			{summaryAnchorModel && summaryAnchorModel.state !== "none" && (
				<div className="field-group">
					<label className="field-label">{txt("anchoredLabel")}</label>
					<AnchorDisplayBlock
						model={summaryAnchorModel}
						previewModel={showAnchorPreview ? previewAnchorModel : null}
						showPreviewLabel
					/>
				</div>
			)}

			<div className="correction-actions">
				<button
					className="btn btn-primary"
					disabled={submitting || !hasCorrectionChanges}
					onClick={onSubmit}
				>
					{submitting ? txt("submitting") : txt("submitCorrection")}
				</button>
				<button
					className="btn btn-ghost"
					disabled={submitting}
					onClick={onCancel}
				>
					{txt("cancelCorrection")}
				</button>
			</div>
		</div>
	);
}
